import React, { useEffect, useMemo, useState } from 'react';
import {
  FaCalendar,
  FaCheck,
  FaChevronRight,
  FaCopy,
  FaEye,
  FaEyeSlash,
  FaKey,
  FaPlug,
  FaServer,
  FaSyncAlt,
  FaTags,
  FaListUl,
  FaTrash,
  FaExclamationTriangle,
  FaBroom,
  FaCheckCircle,
} from 'react-icons/fa';
import {
  buildTenantEditDraft,
  buildTenantEditPayload,
  formatRemainingTime,
  formatShortTime,
  getDisplayUrl,
  getDrawerOperationalActions,
  getTenantCredentials,
  isFieldChanged,
  isTenantEditDirty,
  isTenantIntegrationEditable,
  validateTenantEditDraft,
} from '../tenantHelpers';

const SECRET_FIELDS = [
  { key: 'password', label: 'Password' },
  { key: 'apiKey', label: 'API Key' },
  { key: 'accessKey', label: 'Access Key' },
  { key: 'secretKey', label: 'Secret Key' },
];

const INTEGRATION_SECTIONS = [
  {
    key: 'jira',
    label: 'Jira',
    isConfigured: (draft) => Boolean(draft?.jiraUrl?.trim() || draft?.jiraUsername?.trim()),
    viewFields: (creds) => [
      { label: 'URL', value: getDisplayUrl(creds.jira.url), copy: creds.jira.url },
      { label: 'Username', value: creds.jira.username, copy: creds.jira.username },
      { label: 'Password', value: creds.jira.password, secret: true },
    ],
    editFields: [
      { key: 'jiraUrl', label: 'Jira URL' },
      { key: 'jiraUsername', label: 'Jira Username' },
      { key: 'jiraPassword', label: 'Jira Password', secret: true },
    ],
  },
  {
    key: 'catchpoint',
    label: 'Catchpoint',
    isConfigured: (draft) => Boolean(draft?.catchpointUrl?.trim() || draft?.catchpointUsername?.trim()),
    viewFields: (creds) => [
      { label: 'URL', value: getDisplayUrl(creds.catchpoint.url), copy: creds.catchpoint.url },
      { label: 'Username', value: creds.catchpoint.username, copy: creds.catchpoint.username },
      { label: 'Password', value: creds.catchpoint.password, secret: true },
    ],
    editFields: [
      { key: 'catchpointUrl', label: 'Catchpoint URL' },
      { key: 'catchpointUsername', label: 'Catchpoint Username' },
      { key: 'catchpointPassword', label: 'Catchpoint Password', secret: true },
    ],
  },
  {
    key: 'chatops',
    label: 'Teams Credentials',
    isConfigured: (draft) => Boolean(draft?.chatopsUsername?.trim()),
    viewFields: (creds, tenant) => [
      { label: 'Username', value: creds.chatops.username, copy: creds.chatops.username },
      { label: 'Password', value: creds.chatops.password, secret: true },
      ...(tenant.integrations?.chatOps ? [{ label: 'Channel', value: tenant.integrations.chatOps, copy: tenant.integrations.chatOps }] : []),
    ],
    editFields: [
      { key: 'chatopsUsername', label: 'Teams Username' },
      { key: 'chatopsPassword', label: 'Teams Password', secret: true },
    ],
  },
  {
    key: 'dynatrace',
    label: 'Dynatrace',
    isConfigured: (draft) => Boolean(draft?.dynatraceHost?.trim() || draft?.dynatraceUsername?.trim()),
    viewFields: (creds) => [
      { label: 'Host / URL', value: creds.dynatrace.host, copy: creds.dynatrace.host },
      { label: 'Username', value: creds.dynatrace.username, copy: creds.dynatrace.username },
      { label: 'Password', value: creds.dynatrace.password, secret: true },
    ],
    editFields: [
      { key: 'dynatraceHost', label: 'Dynatrace Host / URL' },
      { key: 'dynatraceUsername', label: 'Dynatrace Username' },
      { key: 'dynatracePassword', label: 'Dynatrace Password', secret: true },
    ],
  },
];

const MAX_VISIBLE_LABELS = 4;

const SECTION_ICONS = {
  details: FaServer,
  booking: FaCalendar,
  access: FaKey,
  integrations: FaPlug,
  operations: FaSyncAlt,
};

const OP_ICONS = {
  labels: FaTags,
  tenantFeatures: FaListUl,
  status: FaSyncAlt,
  resetPasswords: FaKey,
  fault: FaExclamationTriangle,
  unfault: FaCheckCircle,
  cleanup: FaBroom,
  delete: FaTrash,
};

const SectionHeading = ({ icon: Icon, children }) => (
  <h3 className="stm-section-heading">
    {Icon && <Icon className="stm-section-heading__icon" aria-hidden="true" />}
    {children}
  </h3>
);

const IconCopyButton = ({ value, label = 'value' }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!value || !navigator?.clipboard) return;
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (!value) return null;

  return (
    <button
      type="button"
      className={`stm-icon-action${copied ? ' is-copied' : ''}`}
      onClick={handleCopy}
      title={copied ? 'Copied' : `Copy ${label}`}
      aria-label={copied ? 'Copied' : `Copy ${label}`}
    >
      {copied ? <FaCheck /> : <FaCopy />}
    </button>
  );
};

const ViewInlineRow = ({ label, value, copyValue }) => (
  <div className="stm-view-row stm-view-row--inline">
    <span className="stm-view-row__label">{label}</span>
    <div className="stm-view-row__value">
      <span>{value || '—'}</span>
      {copyValue && value && <IconCopyButton value={copyValue} label={label.toLowerCase()} />}
    </div>
  </div>
);

const ViewStackRow = ({ label, value, copyValue, highlight }) => (
  <div className={`stm-view-row stm-view-row--stack${highlight ? ' stm-view-row--highlight' : ''}`}>
    <span className="stm-view-row__label">{label}</span>
    <div className={`stm-view-row__value stm-view-row__value--stack${highlight ? ' stm-view-row__value--highlight' : ''}`}>
      <span className="stm-view-row__text">{value || '—'}</span>
      {copyValue && value && <IconCopyButton value={copyValue} label={label.toLowerCase()} />}
    </div>
  </div>
);

const ViewSecretStackRow = ({ label, value }) => {
  const [revealed, setRevealed] = useState(false);
  const actionLabel = label.toLowerCase();

  return (
    <div className="stm-view-row stm-view-row--stack stm-view-row--secret">
      <span className="stm-view-row__label">{label}</span>
      <div className="stm-view-row__value stm-view-row__value--stack">
        <span className="stm-view-row__text">{revealed ? value : '••••••••••••'}</span>
        <div className="stm-view-row__icon-actions">
          <button
            type="button"
            className="stm-icon-action"
            onClick={() => setRevealed((prev) => !prev)}
            title={revealed ? `Hide ${actionLabel}` : `Reveal ${actionLabel}`}
            aria-label={revealed ? `Hide ${actionLabel}` : `Reveal ${actionLabel}`}
          >
            {revealed ? <FaEyeSlash /> : <FaEye />}
          </button>
          <IconCopyButton value={value} label={actionLabel} />
        </div>
      </div>
    </div>
  );
};

const SecretEditField = ({
  label,
  fieldKey,
  value,
  isEditing,
  isChanged,
  revealed,
  error,
  onStartChange,
  onCancelChange,
  onChange,
  onToggleReveal,
}) => (
  <div className={`stm-edit-secret ${isChanged ? 'is-changed' : ''}`}>
    <span className="stm-field__label">{label}</span>
    {!isEditing ? (
      <div className="stm-edit-secret__existing">
        <span className="stm-edit-secret__masked">••••••••••••</span>
        <button type="button" className="stm-edit-secret__change-btn" onClick={onStartChange}>
          Change {label.toLowerCase()}
        </button>
      </div>
    ) : (
      <div className="stm-edit-secret__input-wrap">
        <div className="stm-password-input">
          <input
            type={revealed ? 'text' : 'password'}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder={`Enter new ${label.toLowerCase()}`}
            autoComplete="new-password"
          />
          <button type="button" className="stm-icon-btn stm-icon-btn--ghost" onClick={onToggleReveal} aria-label={`Toggle ${label}`}>
            {revealed ? <FaEyeSlash /> : <FaEye />}
          </button>
        </div>
        <button type="button" className="stm-edit-secret__cancel-btn" onClick={onCancelChange}>
          Cancel change
        </button>
      </div>
    )}
    {error && <span className="stm-field-error">{error}</span>}
  </div>
);

const TenantDrawerContent = ({
  tenant,
  isEditing,
  onSave,
  onDirtyChange,
  onAction,
  onManageTenantFeatures,
}) => {
  const [draft, setDraft] = useState(null);
  const [editingSecrets, setEditingSecrets] = useState({});
  const [revealedSecrets, setRevealedSecrets] = useState({});
  const [errors, setErrors] = useState({});
  const [openIntegration, setOpenIntegration] = useState('');

  useEffect(() => {
    if (!tenant) return;
    setDraft(buildTenantEditDraft(tenant));
    setEditingSecrets({});
    setRevealedSecrets({});
    setErrors({});
    setOpenIntegration('');
  }, [tenant, isEditing]);

  const creds = useMemo(() => getTenantCredentials(tenant), [tenant]);
  const operationalActions = useMemo(() => getDrawerOperationalActions(tenant), [tenant]);
  const booking = tenant?.currentBooking;
  const labels = tenant?.labels || [];
  const visibleLabels = labels.slice(0, MAX_VISIBLE_LABELS);
  const hiddenLabelCount = Math.max(0, labels.length - MAX_VISIBLE_LABELS);
  const tenantFeatures = tenant?.tenantFeatures || [];
  const versionFeatures = tenant?.versionFeatures || [];
  const visibleTenantFeatures = tenantFeatures.slice(0, MAX_VISIBLE_LABELS);
  const hiddenTenantFeatureCount = Math.max(0, tenantFeatures.length - MAX_VISIBLE_LABELS);
  const visibleVersionFeatures = versionFeatures.slice(0, MAX_VISIBLE_LABELS);
  const hiddenVersionFeatureCount = Math.max(0, versionFeatures.length - MAX_VISIBLE_LABELS);

  const isDirty = useMemo(() => (
    isEditing && draft ? isTenantEditDirty(tenant, draft, editingSecrets) : false
  ), [isEditing, tenant, draft, editingSecrets]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  if (!draft) return null;

  const updateField = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const startSecretEdit = (field) => {
    setEditingSecrets((prev) => ({ ...prev, [field]: true }));
    setDraft((prev) => ({ ...prev, [field]: '' }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const cancelSecretEdit = (field) => {
    setEditingSecrets((prev) => ({ ...prev, [field]: false }));
    setDraft((prev) => ({ ...prev, [field]: '' }));
    setRevealedSecrets((prev) => ({ ...prev, [field]: false }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const toggleReveal = (field) => {
    setRevealedSecrets((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const isChanged = (field) => isFieldChanged(tenant, draft, field, editingSecrets);

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validateTenantEditDraft(tenant, draft, editingSecrets);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const editPayload = buildTenantEditPayload(tenant, draft, editingSecrets);
    if (!editPayload) return;
    onSave({ draft, editingSecrets });
  };

  const renderTextField = (label, fieldKey) => (
    <label className={`stm-field ${isChanged(fieldKey) ? 'is-changed' : ''}`} key={fieldKey}>
      <span className="stm-field__label">{label}</span>
      <input
        value={draft[fieldKey] || ''}
        onChange={(event) => updateField(fieldKey, event.target.value)}
      />
      {errors[fieldKey] && <span className="stm-field-error">{errors[fieldKey]}</span>}
    </label>
  );

  const renderSecretEdit = (field) => (
    <SecretEditField
      key={field.key}
      label={field.label}
      fieldKey={field.key}
      value={draft[field.key]}
      isEditing={Boolean(editingSecrets[field.key])}
      isChanged={isChanged(field.key)}
      revealed={Boolean(revealedSecrets[field.key])}
      error={errors[field.key]}
      onStartChange={() => startSecretEdit(field.key)}
      onCancelChange={() => cancelSecretEdit(field.key)}
      onChange={(value) => updateField(field.key, value)}
      onToggleReveal={() => toggleReveal(field.key)}
    />
  );

  const isIntegrationConfigured = (section) => {
    if (tenant?.hasDetail) {
      return isTenantIntegrationEditable(tenant, section.key);
    }
    if (section.key === 'jira') return Boolean(creds.jira.url || creds.jira.username);
    if (section.key === 'catchpoint') return Boolean(creds.catchpoint.url || creds.catchpoint.username);
    if (section.key === 'chatops') return Boolean(creds.chatops.username);
    return Boolean(creds.dynatrace.host || creds.dynatrace.username);
  };

  const primaryOps = operationalActions.filter((action) => !action.destructive);
  const destructiveOps = operationalActions.filter((action) => action.destructive);

  const content = (
    <div className="stm-drawer-sections">
      <section className="stm-drawer-section stm-drawer-section--card">
        <SectionHeading icon={SECTION_ICONS.details}>Tenant details</SectionHeading>
        {isEditing ? (
          <div className="stm-edit-fields">
            <div className="stm-edit-form__readonly">
              <div><span>Tenant number</span><strong>{tenant.tenantNumber}</strong></div>
              <div><span>Tenant type</span><strong>{tenant.tenantType}</strong></div>
            </div>
            {renderTextField('Tenant URL', 'tenantUrl')}
            {renderTextField('ITSM URL', 'itsmUrl')}
          </div>
        ) : (
          <>
            <ViewInlineRow label="Tenant number" value={tenant.tenantNumber} />
            <ViewInlineRow label="Tenant type" value={tenant.tenantType} />
            <ViewStackRow label="Tenant URL" value={getDisplayUrl(tenant.tenantUrl)} copyValue={tenant.tenantUrl} highlight />
            <ViewStackRow label="ITSM URL" value={getDisplayUrl(tenant.itsmUrl)} copyValue={tenant.itsmUrl} highlight />
            <div className="stm-view-row stm-view-row--stack stm-view-row--labels">
              <span className="stm-view-row__label">Labels</span>
              <div className="stm-view-row__value stm-view-row__value--start">
                {labels.length ? (
                  <div className="stm-labels-cell">
                    {visibleLabels.map((label) => (
                      <span key={label} className="stm-label-chip">{label}</span>
                    ))}
                    {hiddenLabelCount > 0 && (
                      <span className="stm-label-more">+{hiddenLabelCount}</span>
                    )}
                  </div>
                ) : (
                  <span className="stm-muted">No labels</span>
                )}
              </div>
            </div>
            <div className="stm-view-row stm-view-row--stack stm-view-row--labels">
              <div className="stm-view-row__label-row">
                <span className="stm-view-row__label">Tenant Features</span>
                {!isEditing && onManageTenantFeatures && tenant?.id && (
                  <button
                    type="button"
                    className="stm-text-link"
                    onClick={() => onManageTenantFeatures(tenant.id)}
                  >
                    Manage
                  </button>
                )}
              </div>
              <div className="stm-view-row__value stm-view-row__value--start">
                {tenantFeatures.length ? (
                  <div className="stm-labels-cell">
                    {visibleTenantFeatures.map((feature) => (
                      <span key={feature} className="stm-label-chip">{feature}</span>
                    ))}
                    {hiddenTenantFeatureCount > 0 && (
                      <span className="stm-label-more">+{hiddenTenantFeatureCount}</span>
                    )}
                  </div>
                ) : (
                  <span className="stm-muted">No tenant features configured</span>
                )}
              </div>
            </div>
            <div className="stm-view-row stm-view-row--stack stm-view-row--labels">
              <span className="stm-view-row__label">Version Features</span>
              <div className="stm-view-row__value stm-view-row__value--start">
                {versionFeatures.length ? (
                  <div className="stm-labels-cell">
                    {visibleVersionFeatures.map((feature) => (
                      <span key={feature} className="stm-label-chip">{feature}</span>
                    ))}
                    {hiddenVersionFeatureCount > 0 && (
                      <span className="stm-label-more">+{hiddenVersionFeatureCount}</span>
                    )}
                  </div>
                ) : (
                  <div className="stm-version-features-empty">
                    <span className="stm-muted">No version features configured for the current labels.</span>
                    <span className="stm-view-row__hint">Derived from tenant labels</span>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </section>

      {!isEditing && (
        <section className="stm-drawer-section stm-drawer-section--card">
          <SectionHeading icon={SECTION_ICONS.booking}>Booking</SectionHeading>
          {booking ? (
            <div className="stm-booking-summary">
              <div className="stm-booking-summary__header">
                <span className="stm-booking-summary__name">{booking.bookedUserName}</span>
                {booking.customerName && (
                  <span className="stm-booking-summary__org">{booking.customerName}</span>
                )}
              </div>
              <p className="stm-booking-summary__meta">
                Ends at {formatShortTime(booking.bookingEndTime)}
                {' · '}
                {formatRemainingTime(booking.bookingEndTime) || '—'}
              </p>
            </div>
          ) : (
            <div className="stm-empty-booking">
              <FaCalendar aria-hidden="true" />
              <span>No active booking</span>
            </div>
          )}
        </section>
      )}

      <section className="stm-drawer-section stm-drawer-section--card stm-drawer-section--access">
        <SectionHeading icon={SECTION_ICONS.access}>Tenant access</SectionHeading>
        {isEditing ? (
          <div className="stm-edit-fields">
            {renderTextField('Username', 'username')}
            {SECRET_FIELDS.map(renderSecretEdit)}
          </div>
        ) : (
          <div className="stm-access-list">
            <ViewStackRow label="Username" value={tenant.username} copyValue={tenant.username} />
            <ViewSecretStackRow label="Password" value={creds.password} />
            <ViewSecretStackRow label="API Key" value={creds.apiKey} />
            <ViewSecretStackRow label="Access Key" value={creds.accessKey} />
            <ViewSecretStackRow label="Secret Key" value={creds.secretKey} />
          </div>
        )}
      </section>

      <section className="stm-drawer-section stm-drawer-section--card">
        <SectionHeading icon={SECTION_ICONS.integrations}>Integrations</SectionHeading>
        <div className="stm-accordion stm-accordion--minimal">
          {INTEGRATION_SECTIONS.map((section) => {
            const configured = isIntegrationConfigured(section);
            return (
              <div
                key={section.key}
                className={`stm-accordion__item ${openIntegration === section.key ? 'is-open' : ''}`}
              >
                <button
                  type="button"
                  className="stm-accordion__trigger"
                  onClick={() => setOpenIntegration(openIntegration === section.key ? '' : section.key)}
                  aria-expanded={openIntegration === section.key}
                >
                  <span className="stm-accordion__label">{section.label}</span>
                  <span className="stm-accordion__meta">
                    {configured ? (
                      <span className="stm-accordion__status stm-accordion__status--configured">
                        <span className="stm-accordion__status-dot" aria-hidden="true" />
                        Configured
                      </span>
                    ) : (
                      <span className="stm-accordion__status">Not configured</span>
                    )}
                    <FaChevronRight aria-hidden="true" />
                  </span>
                </button>
                {openIntegration === section.key && (
                  <div className="stm-accordion__body">
                    {isEditing ? (
                      isIntegrationConfigured(section) ? (
                        section.editFields.map((field) => (
                          field.secret
                            ? renderSecretEdit(field)
                            : renderTextField(field.label, field.key)
                        ))
                      ) : (
                        <p className="stm-muted">Not configured for this tenant</p>
                      )
                    ) : (
                      section.viewFields(creds, tenant).map((field) => (
                        field.secret ? (
                          <ViewSecretStackRow key={field.label} label={field.label} value={field.value} />
                        ) : (
                          <ViewStackRow key={field.label} label={field.label} value={field.value} copyValue={field.copy} />
                        )
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {!isEditing && operationalActions.length > 0 && (
        <section className="stm-drawer-section stm-drawer-section--actions">
          <SectionHeading icon={SECTION_ICONS.operations}>Operations</SectionHeading>
          {primaryOps.length > 0 && (
            <div className="stm-ops-list">
              {primaryOps.map((action) => {
                const OpIcon = OP_ICONS[action.key];
                return (
                  <button
                    key={action.key}
                    type="button"
                    className="stm-ops-row"
                    onClick={() => onAction(action.key, tenant)}
                  >
                    {OpIcon && <OpIcon className="stm-ops-row__icon" aria-hidden="true" />}
                    <span className="stm-ops-row__label">{action.label}</span>
                    <FaChevronRight className="stm-ops-row__chevron" aria-hidden="true" />
                  </button>
                );
              })}
            </div>
          )}
          {destructiveOps.length > 0 && (
            <div className="stm-ops-list stm-ops-list--destructive">
              {destructiveOps.map((action) => {
                const OpIcon = OP_ICONS[action.key] || FaTrash;
                return (
                  <button
                    key={action.key}
                    type="button"
                    className="stm-ops-row stm-ops-row--destructive"
                    onClick={() => onAction(action.key, tenant)}
                  >
                    <OpIcon className="stm-ops-row__icon" aria-hidden="true" />
                    <span className="stm-ops-row__label">{action.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </section>
      )}
    </div>
  );

  if (isEditing) {
    return (
      <form id="tenant-drawer-form" className="stm-drawer-form" onSubmit={handleSubmit}>
        {content}
      </form>
    );
  }

  return content;
};

export default TenantDrawerContent;
